import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Spinner, Alert, Button, Input, InputGroup } from "reactstrap";
// PayPal kept for future use (commented out)
// import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ApiClass from "../apiManager/apiClass";
import { getProductById, getProductByPid, HOSTING_NOODLE_GAME_ID } from "../data/products";
import "./PaymentPage.css";

// Set REACT_APP_PAYPAL_CLIENT_ID in .env for production (same as FFM AUTH PayPal client id)
// const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "sb";
const TAX_RATE = 0;

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const authFromUrl = searchParams.get("auth");
  const productIdFromUrl = searchParams.get("product");
  const pidFromUrl = searchParams.get("pid");
  const gidFromUrl = searchParams.get("gid");
  const quantityFromUrl = searchParams.get("quantity");
  const renewalState = location.state;

  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isRenewal, setIsRenewal] = useState(false);
  const [renewalPayload, setRenewalPayload] = useState(null);
  const [error, setError] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherApplied, setVoucherApplied] = useState(false);
  const [newsletterSignup, setNewsletterSignup] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const decodeJwtPayload = (jwt) => {
    try {
      const parts = String(jwt || "").split(".");
      if (parts.length < 2) return null;
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("hosting-noodle-token");
    if (!token && authFromUrl) {
      localStorage.setItem("hosting-noodle-token", authFromUrl);
      // Remove auth token from URL (keep product params)
      const next = new URL(window.location.href);
      next.searchParams.delete("auth");
      window.location.replace(next.toString());
      return;
    }
    if (!token) {
      navigate("/login");
      return;
    }
    if (renewalState?.renew && renewalState?.licenseKey) {
      setIsRenewal(true);
      setRenewalPayload({
        licenseKey: renewalState.licenseKey,
        productId: renewalState.productId,
        gameId: renewalState.gameId,
        quantity: renewalState.quantity || 1,
      });
      setQuantity(renewalState.quantity || 1);
      setProduct({
        id: "renewal",
        name: "Renewal",
        pid: parseInt(renewalState.productId, 10),
        gameID: renewalState.gameId,
      });
      return;
    }
    const resolvedPid = pidFromUrl ? parseInt(pidFromUrl, 10) : null;
    const resolvedGid = gidFromUrl ? parseInt(gidFromUrl, 10) : null;

    let p = null;
    if (productIdFromUrl) p = getProductById(productIdFromUrl);
    if (!p && resolvedPid) p = getProductByPid(resolvedPid);

    if (!p) {
      setError("Product not found.");
      return;
    }
    if (p.trialOnly) {
      setError("Noodle Remote Desktop is not available for purchase. Download the free trial from the homepage.");
      return;
    }
    setProduct(resolvedGid ? { ...p, gameID: resolvedGid } : p);
    setQuantity(quantityFromUrl ? Math.max(1, parseInt(quantityFromUrl, 10) || 1) : 1);
  }, [authFromUrl, productIdFromUrl, pidFromUrl, gidFromUrl, quantityFromUrl, renewalState, navigate]);

  const apiClass = ApiClass.getInstance();

  const startStripeCheckout = async () => {
    try {
      if (stripeLoading) return;
      setStripeLoading(true);
      if (isRenewal) {
        setError("Renewals are automatic with Stripe subscriptions. No action is required.");
        setStripeLoading(false);
        return;
      }
      const gameId = product?.gameID ?? HOSTING_NOODLE_GAME_ID;
      const productId = product?.pid;
      const qty = productId === 7 ? Number(quantity) : 1;
      const res = await apiClass.createStripeCheckoutSession({
        productId,
        quantity: qty,
        gameId,
      });
      const url = res?.msg?.url ?? res?.responseData?.msg?.url;
      if (!url) {
        const backendMsg = typeof res?.msg === "string" ? res.msg : res?.message;
        throw new Error(backendMsg || "Missing Stripe Checkout URL. Check backend env: STRIPE_PROD_PID_1..7, STRIPE_SECRET_KEY.");
      }
      window.location.href = url;
    } catch (err) {
      setError(err?.message || err?.msg || "Failed to start Stripe checkout.");
      setStripeLoading(false);
    }
  };

  const token = typeof window !== "undefined" && localStorage.getItem("hosting-noodle-token");
  if (!token) return null;
  const userProfile = decodeJwtPayload(token);
  const userEmail = userProfile?.email || "";

  if (error) {
    return (
      <>
        <Navbar />
        <main className="hn-payment-main">
          <Container className="py-5">
            <Card className="hn-payment-card hn-payment-error-card">
              <CardBody className="text-center py-5">
                <Alert color="danger" className="hn-payment-error-alert">
                  {error}
                </Alert>
                <Button color="primary" className="hn-payment-back-btn" onClick={() => navigate("/")}>
                  Back to Home
                </Button>
              </CardBody>
            </Card>
          </Container>
        </main>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <main className="hn-payment-main">
          <Container className="py-5">
            <div className="hn-payment-loading text-center py-5">
              <Spinner color="warning" className="mb-3" />
              <p className="text-muted mb-0">Preparing your checkout...</p>
            </div>
          </Container>
        </main>
        <Footer />
      </>
    );
  }

  const displayName = isRenewal ? (renewalState?.displayName || "Renewal") : product.name;
  const showPrice = !isRenewal && product.price != null;
  const unitPrice = showPrice ? product.price : null;
  const subtotal = showPrice ? product.price * quantity : 0;
  const discount = 0;
  const tax = subtotal * TAX_RATE;
  const total = subtotal - discount + tax;
  const unitPriceLabel = showPrice && product.period
    ? `£${Number(product.price).toFixed(2)} / ${product.period}`
    : showPrice
      ? `£${Number(product.price).toFixed(2)}`
      : null;

  const handleApplyVoucher = () => {
    if (voucherCode.trim()) setVoucherApplied(true);
  };

  return (
    <>
      <Navbar />
      <main className="hn-payment-main">
        <Container className="py-4 py-md-5">
          <div className="hn-payment-header">
            <h1 className="hn-payment-title">Secure checkout</h1>
            <p className="hn-payment-subtitle">Complete your subscription with Stripe</p>
          </div>
          <Row className="justify-content-center">
            <Col lg="10" xl="8">
              <Row className="hn-payment-row">
                <Col md="5" className="mb-4 mb-md-0">
                  <Card className="hn-payment-card hn-payment-summary-card">
                    <CardBody>
                      <h2 className="hn-payment-section-title">Order summary</h2>
                      <div className="hn-payment-summary-item hn-payment-summary-product">
                        <span className="hn-payment-summary-name">{displayName}</span>
                        {!isRenewal && quantity > 1 && (
                          <span className="hn-payment-summary-qty">× {quantity}</span>
                        )}
                      </div>
                      {userEmail && (
                        <div className="hn-payment-summary-line">
                          <span>Email</span>
                          <span>{userEmail}</span>
                        </div>
                      )}
                      {showPrice && (
                        <>
                          <div className="hn-payment-summary-line">
                            <span>Price</span>
                            <span>{unitPriceLabel}</span>
                          </div>
                          <div className="hn-payment-summary-line">
                            <span>Quantity</span>
                            <span>{quantity}</span>
                          </div>
                          <div className="hn-payment-summary-line">
                            <span>Subtotal</span>
                            <span>£{subtotal.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      {isRenewal && (
                        <p className="hn-payment-summary-renewal text-muted small mb-2">
                          Renewal — amount in PayPal
                        </p>
                      )}
                      <div className="hn-payment-voucher">
                        <InputGroup>
                          <Input
                            type="text"
                            placeholder="Voucher or discount code"
                            value={voucherCode}
                            onChange={(e) => setVoucherCode(e.target.value)}
                            className="hn-payment-voucher-input"
                          />
                          <Button color="secondary" outline onClick={handleApplyVoucher}>
                            Apply
                          </Button>
                        </InputGroup>
                        {voucherApplied && (
                          <span className="hn-payment-voucher-msg text-muted small">
                            Code applied. Discount: £0.00
                          </span>
                        )}
                      </div>
                      {showPrice && (
                        <>
                          <div className="hn-payment-summary-line">
                            <span>Discount</span>
                            <span>£{discount.toFixed(2)}</span>
                          </div>
                          <div className="hn-payment-summary-line">
                            <span>Tax (0%)</span>
                            <span>£{tax.toFixed(2)}</span>
                          </div>
                          <div className="hn-payment-summary-line hn-payment-summary-total-line">
                            <span>Total</span>
                            <span>£{total.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      {isRenewal && (
                        <div className="hn-payment-summary-line hn-payment-summary-total-line">
                          <span>Total</span>
                          <span>See PayPal</span>
                        </div>
                      )}
                      <hr className="hn-payment-divider" />
                      <div className="hn-payment-trust">
                        <span className="hn-payment-lock" aria-hidden="true">
                          🔒
                        </span>
                        <span>Your payment is secure and encrypted.</span>
                      </div>
                      <div className="hn-payment-newsletter">
                        <label className="hn-payment-newsletter-label">
                          <Input
                            type="checkbox"
                            checked={newsletterSignup}
                            onChange={(e) => setNewsletterSignup(e.target.checked)}
                            className="hn-payment-newsletter-checkbox"
                          />
                          <span>Sign up to receive news and updates</span>
                        </label>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="7">
                  <Card className="hn-payment-card hn-payment-method-card">
                    <CardBody>
                      <h2 className="hn-payment-section-title">Payment method</h2>
                      <p className="hn-payment-method-desc">
                        You will be redirected to Stripe to complete your subscription securely.
                      </p>
                      <Button color="warning" className="w-100" onClick={startStripeCheckout} disabled={stripeLoading}>
                        {stripeLoading ? "Redirecting to Stripe…" : "Continue to Stripe"}
                      </Button>
                      {/* PayPal kept for future use
                      <PayPalScriptProvider options={{ clientId: PAYPAL_CLIENT_ID, currency: "USD" }}>
                        <PayPalButtons
                          createOrder={createOrder}
                          onApprove={onApprove}
                          onError={(err) => setError(err?.message || "PayPal error")}
                        />
                      </PayPalScriptProvider>
                      */}
                      <div className="hn-payment-actions">
                        <button
                          type="button"
                          className="hn-payment-cancel"
                          onClick={() => navigate("/dashboard")}
                        >
                          Back to dashboard
                        </button>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </main>
      <Footer />
    </>
  );
};

export default PaymentPage;
